import { useInfiniteQuery, useQueries, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import {
  campaignAnalysisListQueryOptions,
  campaignAnalysisPreflightQueryOptions,
  campaignAnalysisResultQueryOptions,
  campaignAnalysisStatusQueryOptions,
  campaignCreatorCountQueryOptions,
  campaignCreatorsQueryOptions,
  campaignDetailQueryOptions,
  campaignListInfiniteQueryOptions,
  campaignMetricsQueryOptions,
  campaignNameAvailabilityQueryOptions,
  campaignOverviewQueryOptions,
  campaignPostCountQueryOptions,
  campaignPostsQueryOptions,
  campaignSlotQueryOptions,
  isCampaignAnalysisTerminal,
  type CampaignCreatorCountRequest,
  type CampaignCreatorListRequest,
  type CampaignPostListData,
  type CampaignPostListRequest,
  type InfiniteCampaignListRequest,
} from '@/entities/campaign';
import { useWorkspaceId } from '@/shared/hooks/use-workspace-id';

const DEFAULT_ANALYSIS_REFETCH_INTERVAL = 2_000;

type EnabledQueryOptions = {
  enabled?: boolean;
};

type CampaignCreatorsQueryOptions = EnabledQueryOptions & {
  pagination?: {
    pageSize: number;
    resetKey?: readonly unknown[];
  };
};

type CampaignPostsQueryOptions = EnabledQueryOptions & {
  mode?: 'page' | 'accumulate';
  pageCount?: number;
};

export function useCampaigns(request: InfiniteCampaignListRequest) {
  const { workspaceId } = useWorkspaceId();
  return useInfiniteQuery(campaignListInfiniteQueryOptions(workspaceId, request));
}

export function useCampaignSlot() {
  const { workspaceId } = useWorkspaceId();
  return useQuery(campaignSlotQueryOptions(workspaceId));
}

export function useCheckCampaignName(name: string, enabled: boolean) {
  const { workspaceId } = useWorkspaceId();
  return useQuery({
    ...campaignNameAvailabilityQueryOptions(workspaceId, name),
    enabled: enabled && name.length > 0,
  });
}

export function useCampaignDetail(campaignId: string) {
  const { workspaceId } = useWorkspaceId();
  return useQuery({
    ...campaignDetailQueryOptions(workspaceId, campaignId),
    enabled: campaignId.length > 0,
  });
}

export function useCampaignOverview(campaignId: string) {
  const { workspaceId } = useWorkspaceId();
  return useQuery(campaignOverviewQueryOptions(workspaceId, campaignId));
}

export function useCampaignMetrics(campaignId: string) {
  const { workspaceId } = useWorkspaceId();
  return useQuery(campaignMetricsQueryOptions(workspaceId, campaignId));
}

export function useCampaignAnalysisList(campaignId: string) {
  const { workspaceId } = useWorkspaceId();
  return useInfiniteQuery({
    ...campaignAnalysisListQueryOptions(workspaceId, campaignId),
    enabled: campaignId.length > 0,
  });
}

export function useCampaignAnalysisPreflight(campaignId: string, options?: EnabledQueryOptions) {
  const { workspaceId } = useWorkspaceId();
  return useQuery({
    ...campaignAnalysisPreflightQueryOptions(workspaceId, campaignId),
    enabled: campaignId.length > 0 && (options?.enabled ?? true),
  });
}

export function useCampaignAnalysisResult(
  campaignId: string,
  runId: string | null | undefined,
  options?: EnabledQueryOptions,
) {
  const { workspaceId } = useWorkspaceId();
  const resolvedRunId = runId ?? '';
  return useQuery({
    ...campaignAnalysisResultQueryOptions(workspaceId, campaignId, resolvedRunId),
    enabled: campaignId.length > 0 && resolvedRunId.length > 0 && (options?.enabled ?? true),
  });
}

export function useCampaignAnalysisStatus(
  campaignId: string,
  runId: string | null | undefined,
  options?: EnabledQueryOptions & { refetchInterval?: number },
) {
  const { workspaceId } = useWorkspaceId();
  const resolvedRunId = runId ?? '';
  return useQuery({
    ...campaignAnalysisStatusQueryOptions(workspaceId, campaignId, resolvedRunId),
    enabled: campaignId.length > 0 && resolvedRunId.length > 0 && (options?.enabled ?? true),
    refetchInterval: (query) =>
      isCampaignAnalysisTerminal(query.state.data?.status)
        ? false
        : (options?.refetchInterval ?? DEFAULT_ANALYSIS_REFETCH_INTERVAL),
  });
}

export function useCampaignCreators(
  campaignId: string,
  pending: boolean,
  request?: CampaignCreatorListRequest,
  options?: CampaignCreatorsQueryOptions,
) {
  const { workspaceId } = useWorkspaceId();
  const [page, setPage] = useState(1);
  const pageSize = options?.pagination?.pageSize;
  const resetKey = options?.pagination?.resetKey;

  useEffect(() => {
    if (resetKey) setPage(1);
  }, [resetKey]);

  const campaignCreatorRequest = useMemo<CampaignCreatorListRequest | undefined>(() => {
    if (!pageSize) return request;
    return { ...request, limit: pageSize, page };
  }, [page, pageSize, request]);

  const query = useQuery({
    ...campaignCreatorsQueryOptions(workspaceId, campaignId, pending, campaignCreatorRequest),
    enabled: campaignId.length > 0 && (options?.enabled ?? true),
  });
  const totalCount = query.data?.pagination.total ?? 0;
  const resolvedPageSize = pageSize ?? query.data?.pagination.limit ?? 0;
  const totalPages =
    resolvedPageSize > 0 ? Math.max(1, Math.ceil(totalCount / resolvedPageSize)) : 1;
  const pageStartIndex = resolvedPageSize > 0 ? (page - 1) * resolvedPageSize : 0;

  return {
    ...query,
    pagination: {
      canGoNext: page < totalPages,
      canGoPrevious: page > 1,
      endIndex: Math.min(pageStartIndex + (query.data?.items.length ?? 0), totalCount),
      goToNextPage: () => setPage((currentPage) => Math.min(currentPage + 1, totalPages)),
      goToPreviousPage: () => setPage((currentPage) => Math.max(currentPage - 1, 1)),
      page,
      pageSize: resolvedPageSize,
      pageStartIndex,
      setPage,
      shouldPaginate: resolvedPageSize > 0 && totalCount > resolvedPageSize,
      startIndex: totalCount > 0 ? pageStartIndex + 1 : 0,
      totalCount,
      totalPages,
    },
  };
}

export function useCampaignCreatorCounts(
  campaignId: string,
  request?: CampaignCreatorCountRequest,
  options?: EnabledQueryOptions,
) {
  const { workspaceId } = useWorkspaceId();
  const enabled = campaignId.length > 0 && (options?.enabled ?? true);
  const [totalQuery, pendingQuery] = useQueries({
    queries: [
      { ...campaignCreatorCountQueryOptions(workspaceId, campaignId, false, request), enabled },
      { ...campaignCreatorCountQueryOptions(workspaceId, campaignId, true, request), enabled },
    ],
  });
  const total = totalQuery.data ?? 0;
  const pending = pendingQuery.data ?? 0;
  const hasAnyData = totalQuery.data != null || pendingQuery.data != null;

  return {
    data: hasAnyData ? { total, pending } : undefined,
    error: totalQuery.error ?? pendingQuery.error ?? null,
    isError: totalQuery.isError || pendingQuery.isError,
    isFetching: totalQuery.isFetching || pendingQuery.isFetching,
    isLoading: totalQuery.isLoading || pendingQuery.isLoading,
    pending,
    pendingQuery,
    refetch: () => Promise.all([totalQuery.refetch(), pendingQuery.refetch()]),
    total,
    totalQuery,
  };
}

export function useCampaignPosts(
  campaignId: string,
  recent: boolean,
  request?: CampaignPostListRequest,
  options?: CampaignPostsQueryOptions,
) {
  const { workspaceId } = useWorkspaceId();
  const mode = options?.mode ?? 'page';
  const pageCount = mode === 'accumulate' ? Math.max(1, options?.pageCount ?? 1) : 1;
  const enabled = campaignId.length > 0 && (options?.enabled ?? true);
  const pageRequests = useMemo(
    () =>
      Array.from(
        { length: pageCount },
        (_, index): CampaignPostListRequest => ({
          sort: request?.sort,
          limit: request?.limit,
          page: mode === 'accumulate' ? index + 1 : (request?.page ?? 1),
        }),
      ),
    [mode, pageCount, request],
  );
  const pageQueries = useQueries({
    queries: pageRequests.map((pageRequest) => ({
      ...campaignPostsQueryOptions(workspaceId, campaignId, recent, pageRequest),
      enabled,
    })),
  });
  const pages = pageQueries.flatMap((query) => (query.data ? [query.data] : []));
  const items = pages.flatMap((page) => page.items);
  const firstPage = pages[0];
  const data: CampaignPostListData | undefined =
    mode === 'page'
      ? firstPage
      : firstPage
        ? { items, pagination: firstPage.pagination }
        : undefined;
  const totalCount = firstPage?.pagination.total ?? 0;
  const lastQuery = pageQueries[pageQueries.length - 1];

  return {
    data,
    error: pageQueries.find((query) => query.error != null)?.error ?? null,
    hasNextPage: mode === 'accumulate' && totalCount > items.length,
    isError: pageQueries.some((query) => query.isError),
    isFetching: pageQueries.some((query) => query.isFetching),
    isFetchingNextPage:
      mode === 'accumulate' &&
      pageQueries.length > 1 &&
      (lastQuery?.isFetching ?? false) &&
      lastQuery?.data == null,
    isLoading:
      mode === 'page'
        ? (pageQueries[0]?.isLoading ?? false)
        : (pageQueries[0]?.isLoading ?? false) && pages.length === 0,
    items,
    pages,
    refetch: () => Promise.all(pageQueries.map((query) => query.refetch())),
    totalCount,
  };
}

export function useCampaignPostCounts(campaignId: string, options?: CampaignPostsQueryOptions) {
  const { workspaceId } = useWorkspaceId();
  const enabled = campaignId.length > 0 && (options?.enabled ?? true);
  const [totalQuery, recentQuery] = useQueries({
    queries: [
      { ...campaignPostCountQueryOptions(workspaceId, campaignId, false), enabled },
      { ...campaignPostCountQueryOptions(workspaceId, campaignId, true), enabled },
    ],
  });
  const total = totalQuery.data ?? 0;
  const recent = recentQuery.data ?? 0;
  const hasAnyData = totalQuery.data != null || recentQuery.data != null;

  return {
    data: hasAnyData ? { total, recent } : undefined,
    error: totalQuery.error ?? recentQuery.error ?? null,
    isError: totalQuery.isError || recentQuery.isError,
    isFetching: totalQuery.isFetching || recentQuery.isFetching,
    isLoading: totalQuery.isLoading || recentQuery.isLoading,
    recent,
    recentQuery,
    refetch: () => Promise.all([totalQuery.refetch(), recentQuery.refetch()]),
    total,
    totalQuery,
  };
}
